/**
 * WorkspaceAssetLibraryPage — `/contributor/workspaces`.
 *
 * The shared asset library of the Workspace the caller is currently acting
 * in (ADR-019). The "workspace" IS the existing Vault org Holder: the
 * selection comes from the global header switcher, and membership, roles
 * and invitations are managed on the existing Workspace controls page —
 * this page only manages the library and which arches may use it.
 *
 * Tabs: Assets · Publish · Linked arches · Settings · Billing (owner only).
 * Role gating mirrors the server (`holder-access.ts`): owner enables /
 * disables and sees billing; admin+ publishes, links, revokes and edits
 * settings; member curates assets; viewer browses.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ApiError,
  type WorkspaceAssetLibraryConfigResult,
  type WorkspaceAssetUsage,
  type WorkspaceLinkedArche,
  type WorkspacePinRefreshResult,
  type WorkspaceRole,
} from '@/lib/api';
import { useSelectedWorkspace, type SelectedWorkspace } from '../hooks/useSelectedWorkspace';
import {
  useWorkspaceAssetLibraryConfig,
  useWorkspaceAssetManifests,
  useWorkspaceLinkedArches,
  useWorkspaceAssetUsage,
  useWorkspaceAssetBillingSummary,
  useEnableWorkspaceAssetLibrary,
  useDisableWorkspaceAssetLibrary,
  useUpdateWorkspaceAssetLibrarySettings,
  usePublishWorkspaceAssetLibrary,
  useRefreshWorkspaceAssetPins,
  useRevokeWorkspaceArcheLink,
} from '../hooks/useWorkspaceAssetLibrary';
import {
  AssetGrid,
  UploadDropzone,
  formatBytes,
  useWorkspaceAssetLibraryAdapter,
} from '../components/asset-library';
import { LinkedArcheValidity } from '../components/asset-library/link-validity';

const WORKSPACE_CONTROLS_PATH = '/workspaces';

interface RoleFlags {
  role: WorkspaceRole;
  isOwner: boolean;
  /** owner or admin */
  isManager: boolean;
  /** owner, admin or member */
  canWrite: boolean;
}

function roleFlags(role: WorkspaceRole): RoleFlags {
  return {
    role,
    isOwner: role === 'owner',
    isManager: role === 'owner' || role === 'admin',
    canWrite: role === 'owner' || role === 'admin' || role === 'member',
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === 'FEATURE_NOT_AVAILABLE') {
    return 'Self-service asset libraries are not yet available on this platform. Contact Fias support to enable one for your workspace.';
  }
  // ROLE_INSUFFICIENT deliberately falls through: the server's message names
  // the exact role requirement for the action, which is more accurate than
  // any generic client copy.
  return err instanceof Error ? err.message : 'Something went wrong.';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function describePublished(published: WorkspaceAssetUsage['published']): string {
  if (!published) return 'Not published yet';
  const count = published.assetCount.toLocaleString();
  return `${count} assets · ${formatBytes(published.totalBytes)} · ${formatDate(published.createdAt)}`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WorkspaceAssetLibraryPage() {
  const ws = useSelectedWorkspace();

  return (
    <div className="container mx-auto p-6">
      <div className="mb-4 text-sm text-muted-foreground">
        <Link to="/contributor/dashboard" className="hover:underline">
          ← Contributor dashboard
        </Link>
      </div>
      {ws.isLoading ? (
        <div className="text-muted-foreground">Loading workspaces…</div>
      ) : ws.isError ? (
        <div className="text-destructive">Could not load your workspaces.</div>
      ) : !ws.holderId ? (
        <PersonalEmptyState orgOptions={ws.orgOptions} onSelectWorkspace={ws.setHolderId} />
      ) : (
        <WorkspaceLibrary holderId={ws.holderId} displayName={ws.displayName ?? 'Workspace'} />
      )}
    </div>
  );
}

function PersonalEmptyState({
  orgOptions,
  onSelectWorkspace,
}: {
  orgOptions: SelectedWorkspace['orgOptions'];
  onSelectWorkspace: (holderId: string | null) => void;
}) {
  const hasOrgs = orgOptions.length > 0;
  return (
    <>
      <h1 className="text-2xl font-bold">Workspace Assets</h1>
      <Card className="mt-6">
        <CardContent className="pt-6">
          <div className="font-medium">No organization workspace selected</div>
          <p className="mt-1 text-sm text-muted-foreground">
            A shared asset library belongs to an organization workspace — a pool of images that
            every arche built by the workspace&apos;s members can use.{' '}
            {hasOrgs
              ? 'Pick one of your organization workspaces to manage its library.'
              : 'You are not a member of any organization workspace yet. Create one, or ask a workspace owner to invite you, then pick it in the workspace switcher in the header.'}
          </p>
          {hasOrgs && (
            <div className="mt-4">
              <label className="block text-sm font-medium" htmlFor="empty-state-workspace-picker">
                Choose a workspace
              </label>
              <select
                id="empty-state-workspace-picker"
                className="mt-1 rounded border border-input bg-background p-2 text-sm"
                value=""
                onChange={(e) => {
                  if (e.target.value) onSelectWorkspace(e.target.value);
                }}
              >
                <option value="">— choose a workspace —</option>
                {orgOptions.map((o) => (
                  <option key={o.holderId} value={o.holderId}>
                    {o.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button variant="outline" className="mt-4" asChild>
            <Link to={WORKSPACE_CONTROLS_PATH}>Create or manage workspaces</Link>
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

function WorkspaceLibrary({ holderId, displayName }: { holderId: string; displayName: string }) {
  const configQuery = useWorkspaceAssetLibraryConfig(holderId);

  if (configQuery.isLoading) {
    return <div className="text-muted-foreground">Loading…</div>;
  }
  if (configQuery.isError || !configQuery.data) {
    return (
      <div className="text-destructive">
        Failed to load this workspace&apos;s asset library: {errorMessage(configQuery.error)}
      </div>
    );
  }

  // The server's role is authoritative (re-derived on every call); the
  // switcher's role is only used for the empty-state hint.
  const flags = roleFlags(configQuery.data.role);
  const name = configQuery.data.holder.displayName || displayName;

  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Workspace Assets</h1>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-lg" data-testid="workspace-name">
              {name}
            </span>
            <Badge variant="secondary" className="capitalize">
              <span className="sr-only">Your role: </span>
              {flags.role}
            </Badge>
          </div>
        </div>
        <Link to={WORKSPACE_CONTROLS_PATH} className="text-sm text-primary hover:underline">
          Workspace controls
        </Link>
      </div>

      <Tabs defaultValue="assets" className="mt-6">
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="assets">Assets</TabsTrigger>
          <TabsTrigger value="publish">Publish</TabsTrigger>
          <TabsTrigger value="arches">Linked arches</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {flags.isOwner && <TabsTrigger value="billing">Billing</TabsTrigger>}
        </TabsList>
        <TabsContent value="assets">
          <AssetsTab holderId={holderId} config={configQuery.data} flags={flags} />
        </TabsContent>
        <TabsContent value="publish">
          <PublishTab holderId={holderId} config={configQuery.data} flags={flags} />
        </TabsContent>
        <TabsContent value="arches">
          <LinkedArchesTab holderId={holderId} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab holderId={holderId} config={configQuery.data} flags={flags} />
        </TabsContent>
        {flags.isOwner && (
          <TabsContent value="billing">
            <BillingTab holderId={holderId} />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

function AssetsTab({
  holderId,
  config,
  flags,
}: {
  holderId: string;
  config: WorkspaceAssetLibraryConfigResult;
  flags: RoleFlags;
}) {
  const adapter = useWorkspaceAssetLibraryAdapter(holderId);
  const enableMutation = useEnableWorkspaceAssetLibrary(holderId);
  const disableMutation = useDisableWorkspaceAssetLibrary(holderId);

  const lib = config.config;
  const isSuspended = !!lib.suspendedAt;
  const isEnabled = lib.enabled;
  const usagePct =
    lib.effectiveCaps.maxStorageBytes > 0
      ? (lib.usage.activeStorageBytes / lib.effectiveCaps.maxStorageBytes) * 100
      : 0;
  const canCurate = flags.canWrite && isEnabled && !isSuspended;
  const toggleError = enableMutation.error ?? disableMutation.error;

  return (
    <div className="space-y-6">
      {isSuspended && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="font-semibold text-destructive">Asset library suspended</div>
            <div className="mt-1 text-sm text-muted-foreground">
              This workspace&apos;s library is suspended due to insufficient credits (
              {lib.suspendReason}). Uploads and publishing are blocked, and linked arches will drop
              these assets at their next publish. Already-pinned versions continue to serve. The
              workspace payer needs to top up to resume.
            </div>
          </CardContent>
        </Card>
      )}

      {!isEnabled && !isSuspended && (
        <Card className="border-muted-foreground/30 bg-muted/30">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            This workspace&apos;s asset library is disabled. Uploads and publishing are blocked, and
            links from arches do not qualify until an owner enables it. Already-pinned versions
            continue to serve.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Asset Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium">{isEnabled ? 'Enabled' : 'Disabled'}</div>
              <div className="text-sm text-muted-foreground">
                {isEnabled
                  ? 'Members can upload and edit. Linked arches pin the latest published version at their next publish.'
                  : 'Uploads are blocked. Only a workspace owner can enable the library.'}
              </div>
            </div>
            {flags.isOwner ? (
              <Switch
                checked={isEnabled}
                onCheckedChange={(next) => {
                  if (next) enableMutation.mutate();
                  else disableMutation.mutate();
                }}
                disabled={enableMutation.isPending || disableMutation.isPending || isSuspended}
                aria-label="Toggle workspace asset library enabled state"
              />
            ) : (
              <Badge variant="outline">Owner-only</Badge>
            )}
          </div>
          {toggleError && (
            <div role="alert" className="mt-2 text-sm text-destructive">
              {errorMessage(toggleError)}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium">Storage</div>
              <div className="text-sm text-muted-foreground">
                {formatBytes(lib.usage.activeStorageBytes)} /{' '}
                {formatBytes(lib.effectiveCaps.maxStorageBytes)} ({usagePct.toFixed(1)}%)
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded bg-muted">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, usagePct)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="text-sm font-medium">Assets</div>
              <div className="text-sm text-muted-foreground">
                {lib.usage.activeAssetCount.toLocaleString()} /{' '}
                {lib.effectiveCaps.maxAssets.toLocaleString()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {canCurate && <UploadDropzone adapter={adapter} />}

      <AssetGrid adapter={adapter} readOnly={!canCurate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Publish
// ---------------------------------------------------------------------------

/**
 * Plain-language outcome for one arche in a pin refresh. Deliberately explains
 * the two "nothing happened, and that is correct" cases rather than hiding
 * them — a manager who sees fewer arches move than they expected needs the
 * reason, not a silent count.
 */
function describePinRefreshOutcome(result: WorkspacePinRefreshResult): string {
  switch (result.outcome) {
    case 'repinned':
      return 'Now serving the latest version.';
    case 'already_current':
      return 'Already on the latest version.';
    case 'skipped_would_drop':
      return `Skipped — the latest version no longer has ${result.missingAssetIds?.length ?? 0} asset${
        result.missingAssetIds?.length === 1 ? '' : 's'
      } this arche is using. Its developer republishes when ready.`;
    case 'not_pinned':
      return 'Linked, but not serving this library yet — it picks the library up when its developer next publishes.';
    case 'no_manifest':
      return 'Its published version went away mid-run — try again.';
    case 'link_invalid':
      return 'The link is no longer valid.';
    case 'unpublished':
      return 'This workspace has no published library version.';
    case 'failed':
      return `Failed: ${result.error ?? 'unknown error'}`;
  }
}

/**
 * Outcomes worth naming, rather than collapsing into a count.
 *
 * `not_pinned` is in here even though nothing happened and nothing can be done
 * about it from this page: for a workspace that has just linked several arches
 * and pushed, "3 other arches needed no change" reads as a bug. Naming them
 * with the reason is the difference between a silent no-op and a refusal the
 * reader understands.
 */
const NOTABLE_PIN_OUTCOMES: ReadonlySet<WorkspacePinRefreshResult['outcome']> = new Set([
  'repinned',
  'skipped_would_drop',
  'not_pinned',
  'failed',
]);

/**
 * Per-arche result of a pin refresh. Notable rows (moved, skipped, failed) are
 * listed in full; the "nothing to do" tail is collapsed to a count so a
 * twenty-arche workspace does not bury the two rows that matter.
 */
function PinRefreshReport({
  data,
}: {
  data: { results: WorkspacePinRefreshResult[]; truncated: boolean };
}) {
  const notable = data.results.filter((r) => NOTABLE_PIN_OUTCOMES.has(r.outcome));
  const quiet = data.results.length - notable.length;

  if (data.results.length === 0) {
    return <p className="mt-2 text-foreground">No arches are linked to this library yet.</p>;
  }

  return (
    <div className="mt-3 space-y-2">
      {notable.length > 0 && (
        <ul className="space-y-1">
          {notable.map((r) => (
            <li key={r.archeId} className="text-foreground">
              <span className="font-medium">{r.archeName ?? r.archeId}</span>{' '}
              <span className="text-muted-foreground">{describePinRefreshOutcome(r)}</span>
            </li>
          ))}
        </ul>
      )}
      {quiet > 0 && (
        <p>
          {quiet} other arche{quiet === 1 ? '' : 's'} needed no change.
        </p>
      )}
      {data.truncated && (
        <p role="alert" className="text-destructive">
          More arches needed updating than one run covers — run it again to finish the rest.
        </p>
      )}
    </div>
  );
}

function PublishTab({
  holderId,
  config,
  flags,
}: {
  holderId: string;
  config: WorkspaceAssetLibraryConfigResult;
  flags: RoleFlags;
}) {
  const usageQuery = useWorkspaceAssetUsage(holderId);
  const manifestsQuery = useWorkspaceAssetManifests(holderId);
  const publishMutation = usePublishWorkspaceAssetLibrary(holderId);
  const refreshPinsMutation = useRefreshWorkspaceAssetPins(holderId);

  const lib = config.config;
  const canPublish = flags.isManager && lib.enabled && !lib.suspendedAt;
  const usage = usageQuery.data;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Publish library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="text-sm text-muted-foreground">
              <p>
                Publishing snapshots the current active assets into a new, immutable library
                version. Arches pin a version at their own publish — a linked arche keeps serving
                the version it pinned until it republishes, so publishing here never changes what a
                live arche shows.
              </p>
              {usage && (
                <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <dt className="font-medium text-foreground">Latest published version</dt>
                    <dd>{describePublished(usage.published)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Current draft</dt>
                    <dd>
                      {usage.draft.activeCount.toLocaleString()} active assets ·{' '}
                      {formatBytes(usage.draft.activeBytes)}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Pinned by published arches</dt>
                    <dd>{formatBytes(usage.pinnedBytes)}</dd>
                  </div>
                </dl>
              )}
              {publishMutation.isSuccess && (
                <p className="mt-2 text-foreground">
                  Published {publishMutation.data.manifest.assetCount.toLocaleString()} asset
                  {publishMutation.data.manifest.assetCount === 1 ? '' : 's'}.
                </p>
              )}
              {publishMutation.isError && (
                <p role="alert" className="mt-2 text-destructive">
                  Could not publish the library: {errorMessage(publishMutation.error)}
                </p>
              )}
            </div>
            {flags.isManager ? (
              <Button
                onClick={() => publishMutation.mutate()}
                disabled={!canPublish || publishMutation.isPending}
              >
                {publishMutation.isPending ? 'Publishing…' : 'Publish library'}
              </Button>
            ) : (
              <Badge variant="outline">Admin-only</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Push to linked arches</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="text-sm text-muted-foreground">
              <p>
                Moves every arche that already serves this library onto the latest published
                version, so an updated sprite reaches live games without their developers
                republishing. An arche is skipped when the latest version no longer has an asset it
                is using — that would break it — and when it is linked but has never served this
                library, since that is its developer&apos;s call to make.
              </p>
              {refreshPinsMutation.isSuccess && (
                <PinRefreshReport data={refreshPinsMutation.data} />
              )}
              {refreshPinsMutation.isError && (
                <p role="alert" className="mt-2 text-destructive">
                  Could not push to linked arches: {errorMessage(refreshPinsMutation.error)}
                </p>
              )}
            </div>
            {flags.isManager ? (
              <Button
                variant="secondary"
                onClick={() => refreshPinsMutation.mutate()}
                disabled={!canPublish || refreshPinsMutation.isPending}
              >
                {refreshPinsMutation.isPending ? 'Pushing…' : 'Push to linked arches'}
              </Button>
            ) : (
              <Badge variant="outline">Admin-only</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Versions</CardTitle>
        </CardHeader>
        <CardContent>
          {manifestsQuery.isLoading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : !manifestsQuery.data || manifestsQuery.data.manifests.length === 0 ? (
            <div className="text-sm text-muted-foreground">No published versions yet.</div>
          ) : (
            <ul className="divide-y rounded border text-sm">
              {manifestsQuery.data.manifests.map((m, idx) => (
                <li
                  key={m.hmManifestId}
                  className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <span className="font-medium">{formatDate(m.createdAt)}</span>
                    {idx === 0 && (
                      <Badge variant="secondary" className="ml-2">
                        Latest
                      </Badge>
                    )}
                    <div className="text-muted-foreground">
                      {m.assetCount.toLocaleString()} assets · {formatBytes(m.totalBytes)}
                    </div>
                  </div>
                  <div className="text-muted-foreground">
                    Pinned by {m.pinCount.toLocaleString()} published arche
                    {m.pinCount === 1 ? '' : 's'}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Versions no arche pins any more are removed automatically at the next publish.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linked arches
// ---------------------------------------------------------------------------

interface PendingRevoke {
  link: WorkspaceLinkedArche;
  mode: 'soft' | 'hard';
}

// Per-link actions are gated by the server-derived `canUnlink` /
// `canHardRevoke` flags on each row — never re-derived from the caller's
// role here (the server owns the rule, e.g. self-serve links a member may
// unlink without being an admin).
function LinkedArchesTab({ holderId }: { holderId: string }) {
  const linksQuery = useWorkspaceLinkedArches(holderId);
  const revokeMutation = useRevokeWorkspaceArcheLink(holderId);
  const [pending, setPending] = React.useState<PendingRevoke | null>(null);
  const [reason, setReason] = React.useState('');

  const links = linksQuery.data?.links ?? [];

  function confirmRevoke() {
    if (!pending) return;
    revokeMutation.mutate(
      {
        archeId: pending.link.archeId,
        mode: pending.mode,
        reason: reason.trim() ? reason.trim() : undefined,
      },
      {
        onSettled: () => {
          setPending(null);
          setReason('');
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Linked arches</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Arches licensed to use this library. A link qualifies only while a workspace member can
            publish the arche — removing that member on the Workspace controls page invalidates the
            link automatically. Arches are linked from their own page in the contributor portal.
          </p>
          {linksQuery.isLoading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : linksQuery.isError ? (
            <div className="text-destructive">Could not load linked arches.</div>
          ) : links.length === 0 ? (
            <div className="text-sm text-muted-foreground">No arches are linked yet.</div>
          ) : (
            <ul className="divide-y rounded border">
              {links.map((link) => (
                <li
                  key={link.archeId}
                  className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{link.archeName ?? 'Unnamed arche'}</div>
                    <div className="mt-1">
                      <LinkedArcheValidity link={link} />
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Linked {formatDate(link.linkedAt)}
                    </div>
                  </div>
                  {/*
                    Unlink is a SOFT revoke, meaningless on an already-revoked
                    row (the server already withholds `canUnlink` there).
                    "Revoke access" stays available while the link is active
                    OR a revoked link is still pinned by a live version — the
                    hard revoke is what stops serving.
                  */}
                  {((link.canUnlink && link.revokedAt === null) || link.canHardRevoke) && (
                    <div className="flex gap-2">
                      {link.canUnlink && link.revokedAt === null && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPending({ link, mode: 'soft' })}
                          disabled={revokeMutation.isPending}
                          aria-label={`Unlink ${link.archeName ?? 'Unnamed arche'}`}
                        >
                          Unlink
                        </Button>
                      )}
                      {link.canHardRevoke && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setPending({ link, mode: 'hard' })}
                          disabled={revokeMutation.isPending}
                          aria-label={`Revoke access for ${link.archeName ?? 'Unnamed arche'}`}
                        >
                          Revoke access
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {revokeMutation.isError && (
            <div role="alert" className="text-sm text-destructive">
              {errorMessage(revokeMutation.error)}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
            setReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.mode === 'hard' ? 'Revoke access for' : 'Unlink'} &ldquo;
              {pending?.link.archeName ?? 'this arche'}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {pending?.mode === 'hard' ? (
                  <>
                    <p>
                      Revoking access deletes every pin this arche holds on this library. Its live
                      published version stops serving these shared assets immediately — end users
                      will see them fail to load until the arche is republished without them.
                    </p>
                    <p>
                      Use this when the assets must not be served any longer (for example, a
                      departed contractor&apos;s plugin).
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Unlinking stops this arche from qualifying at its next publish. Its live
                      published version keeps serving the shared assets it already pinned until the
                      arche is republished.
                    </p>
                    <p>
                      Until then this workspace keeps paying for those pinned bytes. Use
                      &ldquo;Revoke access&rdquo; to stop serving immediately.
                    </p>
                  </>
                )}
                <label className="block text-sm font-medium" htmlFor="revoke-reason">
                  Reason (optional)
                </label>
                <input
                  id="revoke-reason"
                  type="text"
                  maxLength={500}
                  className="w-full rounded border border-input bg-background p-2 text-sm"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={revokeMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                confirmRevoke();
              }}
              className={
                pending?.mode === 'hard'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {revokeMutation.isPending
                ? 'Working…'
                : pending?.mode === 'hard'
                  ? 'Revoke access'
                  : 'Unlink'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

function SettingsTab({
  holderId,
  config,
  flags,
}: {
  holderId: string;
  config: WorkspaceAssetLibraryConfigResult;
  flags: RoleFlags;
}) {
  const settingsMutation = useUpdateWorkspaceAssetLibrarySettings(holderId);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Linking</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium">Members can link their own arches</div>
              <div className="mt-1 text-sm text-muted-foreground">
                When on, any member who can publish an arche may link it to this library themselves.
                When off, only an owner or admin can link an arche.
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Caution: a linked arche&apos;s published version serves these assets through public
                signed URLs. Turning this on lets any member expose the whole library through an
                arche they control. Leave it off unless every member is trusted with the full
                library.
              </div>
              {settingsMutation.isError && (
                <div role="alert" className="mt-2 text-sm text-destructive">
                  {errorMessage(settingsMutation.error)}
                </div>
              )}
            </div>
            {flags.isManager ? (
              <Switch
                checked={config.membersCanLink}
                onCheckedChange={(next) => settingsMutation.mutate({ membersCanLink: next })}
                disabled={settingsMutation.isPending}
                aria-label="Toggle whether members can link their own arches"
              />
            ) : (
              <Badge variant="outline">{config.membersCanLink ? 'On' : 'Off'}</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Funding</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Who pays for the storage of this library&apos;s assets. Changed by Fias platform
              administrators only.
            </div>
            <Badge variant="secondary">
              {config.funding === 'platform' ? 'Platform-funded' : 'Billed to workspace payer'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Members, roles and invitations are managed on the Workspace controls page. Removing a
              member immediately invalidates any link that only they qualified.
            </div>
            <Button variant="outline" asChild>
              <Link to={WORKSPACE_CONTROLS_PATH}>Manage members</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Billing (owner only)
// ---------------------------------------------------------------------------

function BillingTab({ holderId }: { holderId: string }) {
  const billingQuery = useWorkspaceAssetBillingSummary(holderId);

  if (billingQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }
  if (billingQuery.isError || !billingQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="text-destructive">
          Could not load billing: {errorMessage(billingQuery.error)}
        </CardContent>
      </Card>
    );
  }

  const { summary, funding } = billingQuery.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm font-medium">Funding</div>
          <div className="text-sm text-muted-foreground">
            {funding === 'platform'
              ? 'Platform-funded — storage is not charged to this workspace.'
              : 'Billed to the workspace payer — storage is charged daily to the credits of the member who created the workspace.'}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium">Yesterday</div>
          {summary.yesterday ? (
            <div className="text-sm text-muted-foreground">
              Storage: {summary.yesterday.storageChargeCredits.toFixed(4)} credits ·{' '}
              <span className="font-mono">{summary.yesterday.chargeStatus}</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No snapshot yet.</div>
          )}
        </div>
        <div>
          <div className="text-sm font-medium">Month to date</div>
          <div className="text-sm text-muted-foreground">
            {summary.monthToDate.storageChargeCredits.toFixed(4)} credits ·{' '}
            {summary.monthToDate.daysCharged} days charged · {summary.monthToDate.daysFailed} failed
            {' · '}
            {summary.monthToDate.daysAbandoned} abandoned
            {summary.monthToDate.daysPlatformFunded > 0
              ? ` · ${summary.monthToDate.daysPlatformFunded} platform-funded`
              : ''}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
